import React from "react";
import Box from "@mui/material/Box";
import Tab from "@mui/material/Tab";
import Tabs from "@mui/material/Tabs";
import CardDaftarInvoice from "../../../components/CardLaporan/CardDaftarInvoice";
import CardLaporanLabaRugi from "../../../components/CardLaporan/CardLaporanLabaRugi";
import CardMutasiStok from "../../../components/CardLaporan/CardMutasiStok";
import CardPengeluaran from "../../../components/CardLaporan/CardPengeluaran";
import CardStokTerjual from "../../../components/CardLaporan/CardStokTerjual";
import { UserResponse } from "../../../constants/types";
import { LaporanTabKey } from "../hooks/useLaporanFilters";

export type { LaporanTabKey };

interface TabPanelProps {
    children?: React.ReactNode;
    index: number;
    value: number;
}

function TabPanel({ children, value, index, ...other }: TabPanelProps) {
    return (
        <div
            role="tabpanel"
            hidden={value !== index}
            id={`simple-tabpanel-${index}`}
            aria-labelledby={`simple-tab-${index}`}
            {...other}
        >
            {value === index && <Box>{children}</Box>}
        </div>
    );
}

function a11yProps(index: number) {
    return {
        id: `simple-tab-${index}`,
        "aria-controls": `simple-tabpanel-${index}`,
    };
}

interface LaporanTabsProps {
    onTabChange: (slug: LaporanTabKey) => void;
    tabSlug: string;
    tanggalAkhir: string;
    tanggalAwal: string;
    user?: UserResponse["data"];
}

const LaporanTabs = ({
    onTabChange,
    tabSlug,
    tanggalAkhir,
    tanggalAwal,
    user,
}: LaporanTabsProps) => {
    const tabItems = React.useMemo(() => {
        const items = [];

        if (user?.akses.laporan.includes(33)) {
            items.push({
                key: "invoice" as LaporanTabKey,
                label: "Invoice",
                content: (
                    <CardDaftarInvoice
                        tanggalAwal={tanggalAwal}
                        tanggalAkhir={tanggalAkhir}
                    />
                ),
            });
        }

        if (user?.akses.laporan.includes(34)) {
            items.push({
                key: "mutasi" as LaporanTabKey,
                label: "Mutasi",
                content: (
                    <CardMutasiStok
                        tanggalAwal={tanggalAwal}
                        tanggalAkhir={tanggalAkhir}
                    />
                ),
            });
        }

        if (user?.akses.laporan.includes(35)) {
            items.push({
                key: "stokTerjual" as LaporanTabKey,
                label: "Stok Terjual",
                content: (
                    <CardStokTerjual
                        tanggalAwal={tanggalAwal}
                        tanggalAkhir={tanggalAkhir}
                    />
                ),
            });
        }

        if (user?.akses.keuangan.includes(71)) {
            items.push({
                key: "pengeluaran" as LaporanTabKey,
                label: "Pengeluaran",
                content: (
                    <CardPengeluaran
                        tanggalAwal={tanggalAwal}
                        tanggalAkhir={tanggalAkhir}
                    />
                ),
            });
        }

        if (user?.akses.keuangan.includes(72)) {
            items.push({
                key: "labaRugi" as LaporanTabKey,
                label: "Laba Rugi",
                content: (
                    <CardLaporanLabaRugi
                        tanggalAwal={tanggalAwal}
                        tanggalAkhir={tanggalAkhir}
                    />
                ),
            });
        }

        return items;
    }, [tanggalAkhir, tanggalAwal, user]);

    const currentIndex = React.useMemo(() => {
        const idx = tabItems.findIndex((item) => item.key === tabSlug);
        return idx >= 0 ? idx : 0;
    }, [tabItems, tabSlug]);

    const handleChange = (_event: React.SyntheticEvent, newIndex: number) => {
        const item = tabItems[newIndex];
        if (item) onTabChange(item.key);
    };

    return (
        <Box marginTop={3}>
            <Box id="tabs-container">
                <Tabs
                    value={currentIndex}
                    onChange={handleChange}
                    variant="scrollable"
                    scrollButtons="auto"
                    aria-label="basic tabs example"
                    sx={{
                        button: {
                            borderTop: "1px solid rgba(0, 0, 0, 0.12)",
                            borderLeft: "1px solid rgba(0, 0, 0, 0.12)",
                        },
                        "button:first-child": {
                            borderTopLeftRadius: "4px",
                        },
                        "button:last-child": {
                            borderRight: "1px solid rgba(0, 0, 0, 0.12)",
                            borderTopRightRadius: "4px",
                        },
                    }}
                >
                    {tabItems.map((item, index) => (
                        <Tab
                            key={item.key}
                            label={item.label}
                            {...a11yProps(index)}
                        />
                    ))}
                </Tabs>
            </Box>

            {tabItems.map((item, index) => (
                <TabPanel key={item.key} value={currentIndex} index={index}>
                    {item.content}
                </TabPanel>
            ))}
        </Box>
    );
};

export default LaporanTabs;
